<template>
  <div class="pos-page py-3 container-fluid">

    <!-- Panel: caja cerrada (se muestra dentro del cuerpo, en lugar de productos/carrito) -->
    <div v-if="!cashLoaded" class="text-center py-5">
      <i class="fas fa-spinner fa-spin fa-2x text-success"></i>
    </div>

    <div v-if="cashLoaded && !cashSession" class="cash-closed-frame card border-0 shadow-sm">
      <div class="cash-block-card">
        <i class="fas fa-cash-register cash-block-icon"></i>
        <h5 class="mt-3 mb-1">Caja cerrada</h5>
        <p class="text-secondary text-sm mb-4">Debes abrir la caja para comenzar a vender en el POS.</p>
        <label class="form-control-label text-sm">Monto inicial en efectivo</label>
        <input
          v-model.number="openForm.opening_amount"
          type="number"
          min="0"
          step="1"
          class="form-control text-center"
          placeholder="0"
          @keyup.enter="openCash"
        />
        <button
          type="button"
          class="btn btn-success w-100 mt-3"
          :disabled="openingCash"
          @click="openCash"
        >
          <i class="fas" :class="openingCash ? 'fa-spinner fa-spin' : 'fa-door-open'"></i>
          {{ openingCash ? 'Abriendo...' : 'Abrir caja' }}
        </button>
      </div>
    </div>

    <div v-if="cashLoaded && cashSession" class="pos-layout">

      <!-- ===== PRODUCTOS ===== -->
      <section class="pos-products card border-0 shadow-sm">
        <div class="card-body">
          <div class="pos-products-header mb-3">
            <h6 class="text-uppercase text-muted mb-0">Productos</h6>
            <div class="pos-products-toolbar">
              <input
                v-model="search"
                type="text"
                class="form-control form-control-sm pos-search"
                placeholder="Buscar producto o escanear código de barras..."
                @input="onSearch"
                @keyup.enter="onBarcodeSearch"
              />
              <select
                v-model="sourceFilter"
                class="form-select form-select-sm pos-source-select"
                @change="onSourceChange"
              >
                <option value="">Todos</option>
                <option value="local">Local</option>
                <option value="shopify">Shopify</option>
              </select>
              <select
                v-model="priceMode"
                class="form-select form-select-sm pos-source-select"
                title="Tipo de precio"
              >
                <option value="mayor">Mayor</option>
                <option value="detal">Detal</option>
              </select>
              <div class="form-check form-switch d-flex align-items-center gap-1 mb-0 pos-neg-stock-switch" title="Permitir vender productos con stock en cero (el stock no queda negativo)">
                <input class="form-check-input" type="checkbox" id="allowNegStock" v-model="allowNegativeStock" />
                <label class="form-check-label text-xs text-muted" for="allowNegStock">Venta sin stock</label>
              </div>
            </div>
          </div>

          <div v-if="loadingProducts" class="text-center py-5">
            <i class="fas fa-spinner fa-spin fa-2x text-success"></i>
          </div>

          <div v-else class="pos-grid">
            <button
              v-for="product in products"
              :key="product.id"
              type="button"
              class="pos-product-card"
              :class="[
                'pos-product-card--' + (product.stock_level || 'ok'),
                { 'pos-product-card--noimg': !product.image_url || product.imgError }
              ]"
              @click="addToCart(product)"
            >
              <img
                v-if="product.image_url && !product.imgError"
                :src="product.image_url"
                :alt="product.name"
                class="pos-product-img"
                loading="lazy"
                @error="product.imgError = true"
              />
              <i v-else class="fas fa-box-open pos-product-placeholder-icon"></i>
              <span class="pos-source-badge" :class="product.source === 'shopify' ? 'pos-source-badge--shopify' : 'pos-source-badge--local'">
                {{ product.source === 'shopify' ? 'SHOPIFY' : 'LOCAL' }}
              </span>
              <span class="pos-stock-badge">{{ stockLabel(product) }}</span>
              <div class="pos-product-info">
                <p class="pos-product-name">{{ product.name }}</p>
                <span class="pos-product-price">{{ formatCurrency(productPrice(product)) }}</span>
              </div>
            </button>
          </div>

          <div v-if="!loadingProducts && products.length === 0" class="text-center text-muted py-4">
            No hay productos disponibles
          </div>

          <div class="d-flex align-items-center justify-content-between mt-3 pt-2 border-top">
            <span class="text-sm text-muted">
              {{ pagination.from || 0 }}-{{ pagination.to || 0 }} de {{ pagination.total || 0 }}
            </span>
            <div class="pos-pagination">
              <button type="button" class="btn btn-sm btn-outline-secondary" :disabled="pagination.currentPage <= 1" @click="changePage(pagination.currentPage - 1)">&lt;</button>
              <span class="px-2 text-sm">{{ pagination.currentPage }} / {{ pagination.lastPage || 1 }}</span>
              <button type="button" class="btn btn-sm btn-outline-secondary" :disabled="pagination.currentPage >= pagination.lastPage" @click="changePage(pagination.currentPage + 1)">&gt;</button>
            </div>
          </div>
        </div>
      </section>

      <!-- ===== CARRITO ===== -->
      <aside class="pos-cart card border-0 shadow-sm">
        <div class="card-body d-flex flex-column h-100">
          <div class="d-flex align-items-center justify-content-between mb-3">
            <h6 class="text-uppercase mb-0">Carrito de venta</h6>
            <button
              type="button"
              class="btn btn-outline-danger btn-sm py-1 px-2"
              title="Cerrar caja (corte de efectivo)"
              @click="openCloseCash"
            >
              <i class="fas fa-cash-register me-1"></i> Cerrar caja
            </button>
          </div>

          <!-- Tipo de operación: venta normal, cambio o garantía -->
          <div class="pos-sale-mode btn-group btn-group-sm w-100 mb-2" role="group" aria-label="Tipo de operación">
            <button type="button" :class="['btn', saleMode === 'V' ? 'btn-success' : 'btn-outline-secondary']" @click="setSaleMode('V')">Venta</button>
            <button type="button" :class="['btn', saleMode === 'C' ? 'btn-warning' : 'btn-outline-secondary']" @click="setSaleMode('C')">Cambio</button>
            <button type="button" :class="['btn', saleMode === 'G' ? 'btn-danger' : 'btn-outline-secondary']" @click="setSaleMode('G')">Garantía</button>
          </div>
          <p v-if="saleMode === 'C'" class="text-xs text-muted mb-2">
            Marca las prendas devueltas: reingresan al inventario. Las nuevas salen como venta.
          </p>
          <p v-else-if="saleMode === 'G'" class="text-xs text-muted mb-2">
            La prenda nueva se entrega con valor $0 y la dañada se reubica en la Bodega de Imperfectos.
          </p>

          <!-- Cliente -->
          <label class="form-control-label text-sm">Cliente</label>
          <div class="mb-3 pos-client-select-wrap">
            <div class="d-flex gap-2" style="align-items: stretch;">
              <div style="flex: 1; min-width: 0;">
                <select ref="clientSelect" class="form-select form-select-sm w-100"></select>
              </div>
              <button type="button" class="btn btn-sm btn-success d-flex align-items-center justify-content-center" style="flex: 0 0 auto; min-width: 42px; padding: 0 12px; align-self: stretch;margin-bottom:0px !important" @click="openClientModal" title="Crear cliente">
                <i class="fas fa-user-plus"></i>
              </button>
            </div>
          </div>

          <!-- Items carrito -->
          <p class="text-uppercase text-sm text-muted mb-2">Productos</p>
          <div class="pos-cart-items flex-grow-1">
            <p v-if="cart.length === 0" class="text-muted text-sm">El carrito está vacío</p>
            <div v-for="(item, index) in cart" :key="item.cart_key" class="pos-cart-card">
              <!-- Header: badge fuente + nombre + quitar -->
              <div class="pos-cart-card-header">
                <span class="pos-cart-item-badge" :class="item.source === 'shopify' ? 'pos-cart-item-badge--shopify' : 'pos-cart-item-badge--local'">
                  {{ item.source === 'shopify' ? 'SHOPIFY' : 'LOCAL' }}
                </span>
                <span class="pos-cart-card-name">{{ item.name }}</span>
                <button type="button" class="pos-cart-card-remove" @click="removeFromCart(index)">
                  <i class="fas fa-times"></i>
                </button>
              </div>
              <div v-if="item.bodega_id" class="pos-cart-card-bodega">
                <i class="fas fa-warehouse me-1"></i>{{ bodegaName(item.bodega_id) }}
              </div>
              <!-- Rol de la prenda en modos cambio/garantía -->
              <div v-if="saleMode === 'C' || saleMode === 'G'" class="mt-1">
                <select v-model="item.flow_role" class="form-select form-select-sm pos-role-select">
                  <option v-for="opt in roleOptions" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
                </select>
              </div>
              <!-- Qty -->
              <div class="pos-cart-card-qty-row mt-2">
                <div class="pos-cart-card-qty">
                  <button type="button" class="pos-qty-btn" @click="changeQty(index, -1)">−</button>
                  <span class="pos-qty-value">{{ item.quantity }}</span>
                  <button type="button" class="pos-qty-btn" @click="changeQty(index, 1)">+</button>
                </div>
              </div>
              <!-- Precio unitario (no editable) + precio total -->
              <div class="pos-cart-card-prices mt-2">
                <div class="pos-cart-price-field">
                  <span class="pos-cart-price-label">Precio unitario</span>
                  <span class="pos-cart-unit-price-value">{{ formatCurrency(displayUnitPrice(item)) }}</span>
                </div>
                <div class="text-end">
                  <span class="pos-cart-price-label">Precio total</span>
                  <span class="pos-cart-price-total">{{ formatCurrency(lineTotal(item)) }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Totales -->
          <div class="pos-totals mt-2">
            <label v-if="saleMode !== 'G'" class="form-control-label text-sm mb-1">Método de pago</label>
            <select v-if="saleMode !== 'G'" v-model="paymentMethod" class="form-select form-select-sm mb-3">
              <option v-for="m in paymentMethodsList" :key="m" :value="m">{{ m }}</option>
              <option value="PAGOS MÚLTIPLES">PAGOS MÚLTIPLES</option>
            </select>

            <!-- Pagos múltiples -->
            <div v-if="saleMode !== 'G' && isMultiplePayments" class="pos-multiple-payments mb-3">
              <div
                v-for="(p, idx) in multiplePayments"
                :key="'pay-' + idx"
                class="d-flex gap-2 align-items-start mb-2"
              >
                <select v-model="p.payment_method" class="form-select form-select-sm pos-payment-method-select">
                  <option v-for="m in paymentMethodsList" :key="m" :value="m">{{ m }}</option>
                </select>
                <input
                  v-model.number="p.amount"
                  type="number"
                  min="0"
                  step="1"
                  class="form-control form-control-sm pos-payment-amount-input"
                  placeholder="Valor"
                />
                <button
                  v-if="multiplePayments.length > 1"
                  type="button"
                  class="pos-payment-remove"
                  title="Quitar pago"
                  @click="removeMultiplePayment(idx)"
                >
                  <i class="fas fa-times"></i>
                </button>
              </div>
              <button type="button" class="btn btn-xs btn-outline-success mb-2" @click="addMultiplePayment">
                <i class="fas fa-plus me-1"></i>Agregar pago
              </button>
              <div class="d-flex justify-content-between text-sm">
                <span>Total pagado</span>
                <span class="font-weight-bold">{{ formatCurrency(multiplePaymentsTotal) }}</span>
              </div>
              <div v-if="multiplePending > 0" class="d-flex justify-content-between text-sm text-danger mt-1">
                <span>Faltante</span><span>{{ formatCurrency(multiplePending) }}</span>
              </div>
              <div v-if="change > 0" class="d-flex justify-content-between text-sm text-success mt-1">
                <span>Cambio</span><span>{{ formatCurrency(change) }}</span>
              </div>
            </div>

            <div v-if="saleMode !== 'G'" class="mb-3">
              <label class="form-control-label text-sm mb-1">Evidencia de pago</label>
              <input type="file" accept="image/*" class="form-control form-control-sm" @change="onEvidenceChange" />
              <div v-if="evidencePreview" class="mt-2">
                <img :src="evidencePreview" alt="Evidencia" style="max-width: 100%; max-height: 120px; border-radius: 6px;" />
              </div>
            </div>

            <div v-if="saleMode !== 'G'" class="d-flex align-items-center justify-content-between mb-1">
              <label class="form-control-label text-sm mb-0">Descuento</label>
              <div class="btn-group btn-group-sm">
                <button type="button" :class="['btn btn-xs', discountMode === 'percent' ? 'btn-warning' : 'btn-outline-secondary']" @click="discountMode = 'percent'">%</button>
                <button type="button" :class="['btn btn-xs', discountMode === 'fixed' ? 'btn-warning' : 'btn-outline-secondary']" @click="discountMode = 'fixed'">$</button>
              </div>
            </div>
            <input v-if="saleMode !== 'G'" v-model.number="discountValue" type="number" class="form-control form-control-sm mb-3" min="0" :max="discountMode === 'percent' ? 100 : undefined" step="1" />

            <label v-if="saleMode !== 'G'" class="form-control-label text-sm mb-1">Valor flete</label>
            <input v-if="saleMode !== 'G'" v-model.number="flete" type="number" class="form-control form-control-sm mb-3" min="0" />

            <div class="d-flex justify-content-between text-sm">
              <span>Subtotal</span>
              <span>{{ formatCurrency(cartSubtotal) }}</span>
            </div>
            <div class="d-flex justify-content-between align-items-center text-sm mt-1">
              <span>Valor factura</span>
              <span class="font-weight-bold">{{ formatCurrency(invoiceValue) }}</span>
            </div>
            <div v-if="discountAmount > 0" class="d-flex justify-content-between text-sm mt-1 text-danger">
              <span>Descuento</span><span>−{{ formatCurrency(discountAmount) }}</span>
            </div>
            <div v-if="giftValue > 0" class="d-flex justify-content-between text-sm mt-1">
              <span>Obsequio</span><span>+{{ formatCurrency(giftValue) }}</span>
            </div>
            <div v-if="flete > 0" class="d-flex justify-content-between text-sm mt-1">
              <span>Flete</span><span>+{{ formatCurrency(flete) }}</span>
            </div>
            <div class="d-flex justify-content-between mt-2">
              <span class="font-weight-bold">TOTAL</span>
              <span class="pos-total-amount">{{ formatCurrency(cartTotal) }}</span>
            </div>

            <template v-if="!isMultiplePayments && saleMode !== 'G'">
              <label class="form-control-label text-sm mb-1 mt-3">Valor pagado</label>
              <input v-model.number="amountPaid" type="number" class="form-control form-control-sm mb-2" min="0" />
              <div v-if="change > 0" class="d-flex justify-content-between text-sm text-success">
                <span>Cambio</span><span>{{ formatCurrency(change) }}</span>
              </div>
            </template>

            <label class="form-control-label text-sm mb-1 mt-3">Observación</label>
            <textarea v-model="observation" class="form-control form-control-sm mb-2" rows="2" placeholder="Notas o comentarios..."></textarea>
          </div>

          <div v-if="false" class="mb-2 mt-2">
            <label>
              <input type="checkbox" v-model="isProductionOrder" />
              Pedido de producción
            </label>
          </div>

          <button type="button" class="btn btn-success w-100 mt-3" :disabled="saving || cart.length === 0" @click="saveSale">
            <i class="fas fa-save me-1"></i> {{ saving ? 'Guardando...' : 'Guardar' }}
          </button>
        </div>
      </aside>
    </div>

    <!-- Modal selección de variantes -->
    <div v-if="showVariantModal" class="modal-overlay" @click.self="cancelVariantModal">
      <div class="modal-box card shadow">
        <div class="card-body">
          <h6 class="mb-1">Seleccionar variante</h6>
          <p v-if="pendingProduct" class="text-sm text-muted mb-3">
            <strong>{{ pendingProduct.name }}</strong>
          </p>
          <div class="pos-variant-list">
            <button
              v-for="v in pendingProduct.variants"
              :key="v.id"
              type="button"
              class="pos-variant-btn"
              :class="{ 'pos-variant-btn--selected': selectedVariantId === v.id }"
              @click="selectedVariantId = v.id"
            >
              <span class="pos-variant-title">{{ v.title }}</span>
              <span class="pos-variant-meta">
                <span :class="v.inventory_quantity > 0 ? 'text-success' : 'text-danger'">
                  Stock: {{ v.inventory_quantity }}
                </span>
                &nbsp;·&nbsp;
                <strong>{{ formatCurrency(variantPrice(v)) }}</strong>
              </span>
            </button>
          </div>
          <div class="d-flex gap-2 justify-content-end mt-3">
            <button type="button" class="btn btn-secondary btn-sm" @click="cancelVariantModal">Cancelar</button>
            <button type="button" class="btn btn-success btn-sm" :disabled="!selectedVariantId" @click="confirmVariant">Agregar al carrito</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal crear cliente -->
    <div v-if="showClientModal" class="modal-overlay" @click.self="closeClientModal">
      <div class="modal-box card shadow">
        <div class="card-body">
          <h6 class="mb-3">Crear Cliente</h6>
          <div class="row">
            <div class="col-12 mb-2">
              <label class="form-control-label text-sm">Nombre *</label>
              <input v-model="newClient.name" type="text" class="form-control form-control-sm" />
            </div>
            <div class="col-6 mb-2">
              <label class="form-control-label text-sm">Tipo documento</label>
              <select v-model="newClient.document_type" class="form-select form-select-sm">
                <option value="CC">CC</option>
                <option value="CE">CE</option>
                <option value="TI">TI</option>
                <option value="NIT">NIT</option>
              </select>
            </div>
            <div class="col-6 mb-2">
              <label class="form-control-label text-sm">Documento (cédula) *</label>
              <input v-model="newClient.document" type="text" class="form-control form-control-sm" />
            </div>
            <div class="col-12 mb-2">
              <label class="form-control-label text-sm">Dirección (opcional)</label>
              <input v-model="newClient.address" type="text" class="form-control form-control-sm" />
            </div>
            <div class="col-4 mb-2">
              <label class="form-control-label text-sm">Departamento (opcional)</label>
              <select v-model="newClient.province" class="form-select form-select-sm" @change="onProvinceChange">
                <option value="">Seleccione</option>
                <option v-for="dept in departments" :key="dept.code" :value="dept.name">
                  {{ dept.name }}
                </option>
              </select>
            </div>
            <div class="col-4 mb-2">
              <label class="form-control-label text-sm">Ciudad (opcional)</label>
              <select v-model="newClient.city" class="form-select form-select-sm" :disabled="!newClient.province">
                <option value="">Seleccione</option>
                <option v-for="city in availableCities" :key="city" :value="city">
                  {{ city }}
                </option>
              </select>
            </div>
            <div class="col-4 mb-2">
              <label class="form-control-label text-sm">Código Postal (opcional)</label>
              <input v-model="newClient.zip" type="text" class="form-control form-control-sm" placeholder="Opcional" />
            </div>
            <div class="col-6 mb-2">
              <label class="form-control-label text-sm">Teléfono *</label>
              <input v-model="newClient.phone" type="text" class="form-control form-control-sm" />
            </div>
            <div class="col-6 mb-2">
              <label class="form-control-label text-sm">Email</label>
              <input v-model="newClient.email" type="email" class="form-control form-control-sm" placeholder="Opcional" />
            </div>
          </div>
          <div class="d-flex gap-2 justify-content-end mt-3">
            <button type="button" class="btn btn-secondary btn-sm" @click="closeClientModal">Cancelar</button>
            <button type="button" class="btn btn-success btn-sm" :disabled="savingClient" @click="saveClient">
              {{ savingClient ? 'Guardando...' : 'Guardar' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal cerrar caja -->
    <div v-if="showCloseCash" class="modal-overlay" @click.self="showCloseCash = false">
      <div class="modal-box card shadow">
        <div class="card-body">
          <h6 class="mb-3">Cerrar caja</h6>
          <div class="text-sm text-secondary mb-2">
            Sesión abierta: {{ cashSession ? formatDateTime(cashSession.opened_at) : '—' }} ·
            Monto inicial: {{ formatCurrency(cashSession?.opening_amount) }}
          </div>
          <div class="cash-summary mb-3">
            <div class="d-flex justify-content-between text-sm">
              <span>Ventas desde la apertura</span>
              <span class="font-weight-bold">{{ formatCurrency(cashSummary?.total_sales) }}</span>
            </div>
            <div class="d-flex justify-content-between text-sm mt-1">
              <span>Ventas en efectivo</span>
              <span>{{ formatCurrency(cashSummary?.cash_sales) }}</span>
            </div>
            <div class="d-flex justify-content-between text-sm mt-1">
              <span>Efectivo esperado en caja</span>
              <span class="font-weight-bold">{{ formatCurrency(cashExpected) }}</span>
            </div>
          </div>
          <label class="form-control-label text-sm">Efectivo contado en caja *</label>
          <input
            v-model.number="closeForm.closing_amount"
            type="number"
            min="0"
            step="1"
            class="form-control form-control-sm mb-2"
            placeholder="0"
          />
          <label class="form-control-label text-sm">Observación (opcional)</label>
          <textarea v-model="closeForm.notes" rows="2" class="form-control form-control-sm mb-3" placeholder="Notas del cierre..."></textarea>
          <div class="d-flex gap-2 justify-content-end">
            <button type="button" class="btn btn-secondary btn-sm" @click="showCloseCash = false">Cancelar</button>
            <button type="button" class="btn btn-danger btn-sm" :disabled="closingCash" @click="confirmCloseCash">
              <i class="fas" :class="closingCash ? 'fa-spinner fa-spin' : 'fa-lock'"></i>
              Cerrar caja
            </button>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>
<script>
import posService from '@/services/pos.service';
import settingsService from '@/services/settings.service';
import mediaService from '@/services/media.service';
import {
  savePosTicketData,
  buildTicketPayload,
  openPosTicketWindow,
} from '@/utils/posTicketPrint';
import Swal from 'sweetalert2';
import $ from 'jquery';
import 'select2/dist/css/select2.css';
import 'select2';

export default {
  name: 'Pos',
  data() {
    return {
      search: '',
      searchTimer: null,
      sourceFilter: '',
      bodegaFilter: '',
      priceMode: 'mayor',
      // Tipo de operación: V venta, C cambio, G garantía
      saleMode: 'V',
      // Permite procesar ventas de productos con stock en cero
      allowNegativeStock: false,
      bodegas: [],
      products: [],
      loadingProducts: false,
      pagination: { currentPage: 1, lastPage: 1, total: 0, from: 0, to: 0 },
      clients: [],
      selectedClientId: null,
      cart: [],
      paymentMethodsList: ['EFECTIVO', 'TRANSFERENCIA', 'TARJETA', 'DATAFONO', 'NEQUI', 'BANCOLOMBIA AHORROS', 'BANCOLOMBIA SAS', 'DAVIPLATA', 'ADDI', 'WOMPY'],
      paymentMethod: 'TRANSFERENCIA',
      multiplePayments: [],
      discountMode: 'percent',
      discountValue: 0,
      flete: 0,
      giftValue: 0,
      observation: '',
      isProductionOrder: false,
      saleType: null,
      amountPaid: 0,
      saving: false,
      evidenceFile: null,
      evidencePreview: null,
      showVariantModal: false,
      pendingProduct: null,
      selectedVariantId: null,
      showClientModal: false,
      savingClient: false,
      // Caja (sesión de efectivo)
      cashSession: null,
      cashSummary: null,
      cashLoaded: false,
      openingCash: false,
      openForm: { opening_amount: 0 },
      showCloseCash: false,
      closingCash: false,
      closeForm: { closing_amount: '', notes: '' },
      newClient: {
        name: '',
        document_type: 'CC',
        document: '',
        address: '',
        province: '',
        city: '',
        zip: '',
        phone: '',
        email: '',
        role: '4',
        status: '1',
        payment: '0',
      },
      departments: [
        { code: 'AMA', name: 'Amazonas', cities: ['Leticia', 'Puerto Nariño', 'El Encanto', 'La Chorrera', 'La Pedrera', 'La Victoria', 'Miriti-Paraná', 'Puerto Alegría', 'Puerto Arica', 'Puerto Santander', 'Tarapacá'] },
        { code: 'ANT', name: 'Antioquia', cities: ['Abejorral', 'Abriaquí', 'Alejandría', 'Amagá', 'Amalfi', 'Andes', 'Angelópolis', 'Angostura', 'Anorí', 'Anzá', 'Apartadó', 'Arboletes', 'Argelia', 'Armenia', 'Barbosa', 'Bello', 'Belmira', 'Betania', 'Betulia', 'Briceño', 'Buriticá', 'Cáceres', 'Caicedo', 'Caldas', 'Campamento', 'Cañasgordas', 'Caracolí', 'Caramanta', 'Carepa', 'Carolina del Príncipe', 'Caucasia', 'Chigorodó', 'Cisneros', 'Ciudad Bolívar', 'Cocorná', 'Concepción', 'Concordia', 'Copacabana', 'Dabeiba', 'Donmatías', 'Ebéjico', 'El Bagre', 'El Carmen de Viboral', 'El Peñol', 'El Retiro', 'El Santuario', 'Entrerríos', 'Envigado', 'Fredonia', 'Frontino', 'Giraldo', 'Girardota', 'Gómez Plata', 'Granada', 'Guadalupe', 'Guarne', 'Guatapé', 'Heliconia', 'Hispania', 'Itagüí', 'Ituango', 'Jardín', 'Jericó', 'La Ceja', 'La Estrella', 'La Pintada', 'La Unión', 'Liborina', 'Maceo', 'Marinilla', 'Medellín', 'Montebello', 'Murindó', 'Mutatá', 'Nariño', 'Nechí', 'Necoclí', 'Olaya', 'Peque', 'Pueblorrico', 'Puerto Berrío', 'Puerto Nare', 'Puerto Triunfo', 'Remedios', 'Rionegro', 'Sabanalarga', 'Sabaneta', 'Salgar', 'San Andrés de Cuerquia', 'San Carlos', 'San Francisco', 'San Jerónimo', 'San José de la Montaña', 'San Juan de Urabá', 'San Luis', 'San Pedro de Urabá', 'San Pedro de los Milagros', 'San Rafael', 'San Roque', 'San Vicente', 'Santa Bárbara', 'Santa Fe de Antioquia', 'Santa Rosa de Osos', 'Santo Domingo', 'Segovia', 'Sonsón', 'Sopetrán', 'Támesis', 'Tarazá', 'Tarso', 'Titiribí', 'Toledo', 'Turbo', 'Uramita', 'Urrao', 'Valdivia', 'Valparaíso', 'Vegachí', 'Venecia', 'Vigía del Fuerte', 'Yalí', 'Yarumal', 'Yolombó', 'Yondó', 'Zaragoza'] },
        { code: 'ARU', name: 'Arauca', cities: ['Arauca', 'Arauquita', 'Cravo Norte', 'Fortul', 'Puerto Rondón', 'Saravena', 'Tame'] },
        { code: 'ATL', name: 'Atlántico', cities: ['Baranoa', 'Barranquilla', 'Campo de la Cruz', 'Candelaria', 'Galapa', 'Juan de Acosta', 'Luruaco', 'Malambo', 'Manatí', 'Palmar de Varela', 'Piojó', 'Polonuevo', 'Ponedera', 'Puerto Colombia', 'Repelón', 'Sabanagrande', 'Sabanalarga', 'Santa Lucía', 'Santo Tomás', 'Soledad', 'Suán', 'Tubará', 'Usiacurí'] },
        { code: 'BOG', name: 'Bogotá D.C.', cities: ['Bogotá'] },
        { code: 'BOL', name: 'Bolívar', cities: ['Achí', 'Altos del Rosario', 'Arenal', 'Arjona', 'Arroyohondo', 'Barranco de Loba', 'Brazuelo de Papayal', 'Calamar', 'Cantagallo', 'Cartagena de Indias', 'Cicuco', 'Clemencia', 'Córdoba', 'El Carmen de Bolívar', 'El Guamo', 'El Peñón', 'Hatillo de Loba', 'Magangué', 'Mahates', 'Margarita', 'María la Baja', 'Mompós', 'Montecristo', 'Morales', 'Norosí', 'Pinillos', 'Regidor', 'Río Viejo', 'San Cristóbal', 'San Estanislao', 'San Fernando', 'San Jacinto del Cauca', 'San Jacinto', 'San Juan Nepomuceno', 'San Martín de Loba', 'San Pablo', 'Santa Catalina', 'Santa Rosa', 'Santa Rosa del Sur', 'Simití', 'Soplaviento', 'Talaigua Nuevo', 'Tiquisio', 'Turbaco', 'Turbaná', 'Villanueva', 'Zambrano'] },
        { code: 'BOY', name: 'Boyacá', cities: ['Almeida', 'Aquitania', 'Arcabuco', 'Belén', 'Berbeo', 'Betéitiva', 'Boavita', 'Boyacá', 'Briceño', 'Buenavista', 'Busbanzá', 'Caldas', 'Campohermoso', 'Cerinza', 'Chinavita', 'Chiquinquirá', 'Chíquiza', 'Chiscas', 'Chita', 'Chitaraque', 'Chivatá', 'Chivor', 'Ciénega', 'Cómbita', 'Coper', 'Corrales', 'Covarachía', 'Cubará', 'Cucaita', 'Cuítiva', 'Duitama', 'El Cocuy', 'El Espino', 'Firavitoba', 'Floresta', 'Gachantivá', 'Gámeza', 'Garagoa', 'Guacamayas', 'Guateque', 'Guayatá', 'Güicán', 'Iza', 'Jenesano', 'Jericó', 'La Capilla', 'La Uvita', 'La Victoria', 'Labranzagrande', 'Macanal', 'Maripí', 'Miraflores', 'Mongua', 'Monguí', 'Moniquirá', 'Motavita', 'Muzo', 'Nobsa', 'Nuevo Colón', 'Oicatá', 'Otanche', 'Pachavita', 'Páez', 'Paipa', 'Pajarito', 'Panqueba', 'Pauna', 'Paya', 'Paz del Río', 'Pesca', 'Pisba', 'Puerto Boyacá', 'Quípama', 'Ramiriquí', 'Ráquira', 'Rondón', 'Saboyá', 'Sáchica', 'Samacá', 'San Eduardo', 'San José de Pare', 'San Luis de Gaceno', 'San Mateo', 'San Miguel de Sema', 'San Pablo de Borbur', 'Santa María', 'Santa Rosa de Viterbo', 'Santa Sofía', 'Santana', 'Sativanorte', 'Sativasur', 'Siachoque', 'Soatá', 'Socha', 'Socotá', 'Sogamoso', 'Somondoco', 'Sora', 'Soracá', 'Sotaquirá', 'Susacón', 'Sutamarchán', 'Sutatenza', 'Tasco', 'Tenza', 'Tibaná', 'Tibasosa', 'Tinjacá', 'Tipacoque', 'Toca', 'Togüí', 'Tópaga', 'Tota', 'Tunja', 'Tununguá', 'Turmequé', 'Tuta', 'Tutazá', 'Úmbita', 'Ventaquemada', 'Villa de Leyva', 'Viracachá', 'Zetaquira'] },
        { code: 'CAL', name: 'Caldas', cities: ['Aguadas', 'Anserma', 'Aranzazu', 'Belalcázar', 'Chinchiná', 'Filadelfia', 'La Dorada', 'La Merced', 'Manizales', 'Manzanares', 'Marmato', 'Marquetalia', 'Marulanda', 'Neira', 'Norcasia', 'Pácora', 'Palestina', 'Pensilvania', 'Riosucio', 'Risaralda', 'Salamina', 'Samaná', 'San José', 'Supía', 'Victoria', 'Villamaría', 'Viterbo'] },
        { code: 'CAQ', name: 'Caquetá', cities: ['Albania', 'Belén de los Andaquíes', 'Cartagena del Chairá', 'Curillo', 'El Doncello', 'El Paujil', 'Florencia', 'La Montañita', 'Milán', 'Morelia', 'Puerto Rico', 'San José del Fragua', 'San Vicente del Caguán', 'Solano', 'Solita', 'Valparaíso'] },
        { code: 'CAS', name: 'Casanare', cities: ['Aguazul', 'Chámeza', 'Hato Corozal', 'La Salina', 'Maní', 'Monterrey', 'Nunchía', 'Orocué', 'Paz de Ariporo', 'Pore', 'Recetor', 'Sabanalarga', 'Sácama', 'San Luis de Palenque', 'Támara', 'Tauramena', 'Trinidad', 'Villanueva', 'Yopal'] },
        { code: 'CAU', name: 'Cauca', cities: ['Almaguer', 'Argelia', 'Balboa', 'Bolívar', 'Buenos Aires', 'Cajibío', 'Caldono', 'Caloto', 'Corinto', 'El Tambo', 'Florencia', 'Guachené', 'Guapí', 'Inzá', 'Jambaló', 'La Sierra', 'La Vega', 'López de Micay', 'Mercaderes', 'Miranda', 'Morales', 'Padilla', 'Páez', 'Patía', 'Piamonte', 'Piendamó', 'Popayán', 'Puerto Tejada', 'Puracé', 'Rosas', 'San Sebastián', 'Santa Rosa', 'Santander de Quilichao', 'Silvia', 'Sotará', 'Suárez', 'Sucre', 'Timbío', 'Timbiquí', 'Toribío', 'Totoró', 'Villa Rica'] },
        { code: 'CES', name: 'Cesar', cities: ['Aguachica', 'Agustín Codazzi', 'Astrea', 'Becerril', 'Bosconia', 'Chimichagua', 'Chiriguaná', 'Curumaní', 'El Copey', 'El Paso', 'Gamarra', 'González', 'La Gloria (Cesar)', 'La Jagua de Ibirico', 'La Paz', 'Manaure Balcón del Cesar', 'Pailitas', 'Pelaya', 'Pueblo Bello', 'Río de Oro', 'San Alberto', 'San Diego', 'San Martín', 'Tamalameque', 'Valledupar'] },
        { code: 'CHO', name: 'Chocó', cities: ['Acandí', 'Alto Baudó', 'Bagadó', 'Bahía Solano', 'Bajo Baudó', 'Bojayá', 'Cantón de San Pablo', 'Cértegui', 'Condoto', 'El Atrato', 'El Carmen de Atrato', 'El Carmen del Darién', 'Istmina', 'Juradó', 'Litoral de San Juan', 'Lloró', 'Medio Atrato', 'Medio Baudó', 'Medio San Juan', 'Nóvita', 'Nuquí', 'Quibdó', 'Río Iró', 'Río Quito', 'Riosucio', 'San José del Palmar', 'Sipí', 'Tadó', 'Unión Panamericana', 'Unguía', 'Nuevo Belén de Bajirá'] },
        { code: 'COR', name: 'Córdoba', cities: ['Ayapel', 'Buenavista', 'Canalete', 'Cereté', 'Chimá', 'Chinú', 'Ciénaga de Oro', 'Cotorra', 'La Apartada', 'Lorica', 'Los Córdobas', 'Momil', 'Montelíbano', 'Montería', 'Moñitos', 'Planeta Rica', 'Pueblo Nuevo', 'Puerto Escondido', 'Puerto Libertador', 'Purísima', 'Sahagún', 'San Andrés de Sotavento', 'San Antero', 'San Bernardo del Viento', 'San Carlos', 'San José de Uré', 'San Pelayo', 'Tierralta', 'Tuchín', 'Valencia'] },
        { code: 'CUN', name: 'Cundinamarca', cities: ['Agua de Dios', 'Albán', 'Anapoima', 'Anolaima', 'Apulo', 'Arbeláez', 'Beltrán', 'Bituima', 'Bojacá', 'Cabrera', 'Cachipay', 'Cajicá', 'Caparrapí', 'Cáqueza', 'Carmen de Carupa', 'Chaguaní', 'Chía', 'Chipaque', 'Choachí', 'Chocontá', 'Cogua', 'Cota', 'Cucunubá', 'El Colegio', 'El Peñón', 'El Rosal', 'Facatativá', 'Fómeque', 'Fosca', 'Funza', 'Fúquene', 'Fusagasugá', 'Gachalá', 'Gachancipá', 'Gachetá', 'Gama', 'Girardot', 'Granada', 'Guachetá', 'Guaduas', 'Guasca', 'Guataquí', 'Guatavita', 'Guayabal de Síquima', 'Guayabetal', 'Gutiérrez', 'Jerusalén', 'Junín', 'La Calera', 'La Mesa', 'La Palma', 'La Peña', 'La Vega', 'Lenguazaque', 'Machetá', 'Madrid', 'Manta', 'Medina', 'Mosquera', 'Nariño', 'Nemocón', 'Nilo', 'Nimaima', 'Nocaima', 'Pacho', 'Paime', 'Pandi', 'Paratebueno', 'Pasca', 'Puerto Salgar', 'Pulí', 'Quebradanegra', 'Quetame', 'Quipile', 'Ricaurte', 'San Antonio del Tequendama', 'San Bernardo', 'San Cayetano', 'San Francisco', 'San Juan de Rioseco', 'Sasaima', 'Sesquilé', 'Sibaté', 'Silvania', 'Simijaca', 'Soacha', 'Sopó', 'Subachoque', 'Suesca', 'Supatá', 'Susa', 'Sutatausa', 'Tabio', 'Tausa', 'Tena', 'Tenjo', 'Tibacuy', 'Tibirita', 'Tocaima', 'Tocancipá', 'Topaipí', 'Ubalá', 'Ubaque', 'Ubaté', 'Une', 'Útica', 'Venecia', 'Vergara', 'Vianí', 'Villagómez', 'Villapinzón', 'Villeta', 'Viotá', 'Yacopí', 'Zipacón', 'Zipaquirá'] },
        { code: 'GUA', name: 'Guainía', cities: ['Inírida', 'Barranco Minas', 'Mapiripana', 'San Felipe', 'Puerto Colombia', 'La Guadalupe', 'Cacahual', 'Pana Pana', 'Morichal'] },
        { code: 'GUV', name: 'Guaviare', cities: ['Calamar', 'El Retorno', 'Miraflores', 'San José del Guaviare', 'San Joaquín', 'La Macarena'] },
        { code: 'HUI', name: 'Huila', cities: ['Acevedo', 'Agrado', 'Aipe', 'Algeciras', 'Altamira', 'Baraya', 'Campoalegre', 'Colombia', 'El Pital', 'Elías', 'Garzón', 'Gigante', 'Guadalupe', 'Hobo', 'Íquira', 'Isnos', 'La Argentina', 'La Plata', 'Nátaga', 'Neiva', 'Oporapa', 'Paicol', 'Palermo', 'Palestina', 'Pitalito', 'Rivera', 'Saladoblanco', 'San Agustín', 'Santa María', 'Suaza', 'Tarqui', 'Tello', 'Teruel', 'Tesalia', 'Timaná', 'Villavieja', 'Yaguará'] },
        { code: 'LAG', name: 'La Guajira', cities: ['Albania', 'Barrancas', 'Dibulla', 'Distracción', 'El Molino', 'Fonseca', 'Hatonuevo', 'La Jagua del Pilar', 'Maicao', 'Manaure', 'Riohacha', 'San Juan del Cesar', 'Uribia', 'Urumita', 'Villanueva'] },
        { code: 'MAG', name: 'Magdalena', cities: ['Algarrobo', 'Aracataca', 'Ariguaní', 'Cerro de San Antonio', 'Chibolo', 'Ciénaga', 'Concordia', 'El Banco', 'El Piñón', 'El Retén', 'Fundación', 'Guamal', 'Nueva Granada', 'Pedraza', 'Pijiño del Carmen', 'Pivijay', 'Plato', 'Pueblo Viejo', 'Remolino', 'Sabanas de San Ángel', 'Salamina', 'San Sebastián de Buenavista', 'San Zenón', 'Santa Ana', 'Santa Bárbara de Pinto', 'Santa Marta', 'Sitionuevo', 'Tenerife', 'Zapayán', 'Zona Bananera'] },
        { code: 'MET', name: 'Meta', cities: ['Acacías', 'Barranca de Upía', 'Cabuyaro', 'Castilla la Nueva', 'Cubarral', 'Cumaral', 'El Calvario', 'El Castillo', 'El Dorado', 'Fuente de Oro', 'Granada', 'Guamal', 'La Macarena', 'La Uribe', 'Lejanías', 'Mapiripán', 'Mesetas', 'Puerto Concordia', 'Puerto Gaitán', 'Puerto Lleras', 'Puerto López', 'Puerto Rico', 'Restrepo', 'San Carlos de Guaroa', 'San Juan de Arama', 'San Juanito', 'San Martín', 'Villavicencio', 'Vista Hermosa'] },
        { code: 'NAR', name: 'Nariño', cities: ['Aldana', 'Ancuyá', 'Arboleda', 'Barbacoas', 'Belén', 'Buesaco', 'Chachagüí', 'Colón', 'Consacá', 'Contadero', 'Córdoba', 'Cuaspud', 'Cumbal', 'Cumbitara', 'El Charco', 'El Peñol', 'El Rosario', 'El Tablón', 'El Tambo', 'Francisco Pizarro', 'Funes', 'Guachucal', 'Guaitarilla', 'Gualmatán', 'Iles', 'Imués', 'Ipiales', 'La Cruz', 'La Florida', 'La Llanada', 'La Tola', 'La Unión', 'Leiva', 'Linares', 'Los Andes', 'Magüí Payán', 'Mallama', 'Mosquera', 'Nariño', 'Olaya Herrera', 'Ospina', 'Pasto', 'Policarpa', 'Potosí', 'Providencia', 'Puerres', 'Pupiales', 'Ricaurte', 'Roberto Payán', 'Samaniego', 'San Bernardo', 'San José de Albán', 'San Lorenzo', 'San Pablo', 'San Pedro de Cartago', 'Sandoná', 'Santa Bárbara', 'Santacruz', 'Sapuyes', 'Taminango', 'Tangua', 'Tumaco', 'Túquerres', 'Yacuanquer'] },
        { code: 'NSA', name: 'Norte de Santander', cities: ['Ábrego', 'Arboledas', 'Bochalema', 'Bucarasica', 'Cáchira', 'Cácota', 'Chinácota', 'Chitagá', 'Convención', 'Cúcuta', 'Cucutilla', 'Duranía', 'El Carmen', 'El Tarra', 'El Zulia', 'Gramalote', 'Hacarí', 'Herrán', 'La Esperanza', 'La Playa de Belén', 'Labateca', 'Los Patios', 'Lourdes', 'Mutiscua', 'Ocaña', 'Pamplona', 'Pamplonita', 'Puerto Santander', 'Ragonvalia', 'Salazar de Las Palmas', 'San Calixto', 'San Cayetano', 'Santiago', 'Santo Domingo de Silos', 'Sardinata', 'Teorama', 'Tibú', 'Toledo', 'Villa Caro', 'Villa del Rosario'] },
        { code: 'PUT', name: 'Putumayo', cities: ['Colón', 'Mocoa', 'Orito', 'Puerto Asís', 'Puerto Caicedo', 'Puerto Guzmán', 'Puerto Leguízamo', 'San Francisco', 'San Miguel', 'Santiago', 'Sibundoy', 'Valle del Guamuez', 'Villagarzón'] },
        { code: 'QUI', name: 'Quindío', cities: ['Armenia', 'Buenavista', 'Calarcá', 'Circasia', 'Córdoba', 'Filandia', 'Génova', 'La Tebaida', 'Montenegro', 'Pijao', 'Quimbaya', 'Salento'] },
        { code: 'RIS', name: 'Risaralda', cities: ['Apía', 'Balboa', 'Belén de Umbría', 'Dosquebradas', 'Guática', 'La Celia', 'La Virginia', 'Marsella', 'Mistrató', 'Pereira', 'Pueblo Rico', 'Quinchía', 'Santa Rosa de Cabal', 'Santuario', 'Marambá'] },
        { code: 'SAP', name: 'San Andrés y Providencia', cities: ['Providencia y Santa Catalina Islas', 'San Andrés'] },
        { code: 'SAN', name: 'Santander', cities: ['Aguada', 'Albania', 'Aratoca', 'Barbosa', 'Barichara', 'Barrancabermeja', 'Betulia', 'Bolívar', 'Bucaramanga', 'Cabrera', 'California', 'Capitanejo', 'Carcasí', 'Cepitá', 'Cerrito', 'Charalá', 'Charta', 'Chima', 'Chipatá', 'Cimitarra', 'Concepción', 'Confines', 'Contratación', 'Coromoro', 'Curití', 'El Carmen de Chucurí', 'El Guacamayo', 'El Peñón', 'El Playón', 'El Socorro', 'Encino', 'Enciso', 'Florián', 'Floridablanca', 'Galán', 'Gámbita', 'Girón', 'Guaca', 'Guadalupe', 'Guapotá', 'Guavatá', 'Güepsa', 'Hato', 'Jesús María', 'Jordán', 'La Belleza', 'La Paz', 'Landázuri', 'Lebrija', 'Los Santos', 'Macaravita', 'Málaga', 'Matanza', 'Mogotes', 'Molagavita', 'Ocamonte', 'Oiba', 'Onzaga', 'Palmar', 'Palmas del Socorro', 'Páramo', 'Piedecuesta', 'Pinchote', 'Puente Nacional', 'Puerto Parra', 'Puerto Wilches', 'Rionegro', 'Sabana de Torres', 'San Andrés', 'San Benito', 'San Gil', 'San Joaquín', 'San José de Miranda', 'San Miguel', 'San Vicente de Chucurí', 'Santa Bárbara', 'Santa Helena del Opón', 'Simacota', 'Suaita', 'Sucre', 'Suratá', 'Tona', 'Valle de San José', 'Vélez', 'Vetas', 'Villanueva', 'Zapatoca'] },
        { code: 'SUC', name: 'Sucre', cities: ['Buenavista', 'Caimito', 'Chalán', 'Colosó', 'Corozal', 'Coveñas', 'El Roble', 'Galeras', 'Guaranda', 'La Unión', 'Los Palmitos', 'Majagual', 'Morroa', 'Ovejas', 'Sampués', 'San Antonio de Palmito', 'San Benito Abad', 'San Juan de Betulia', 'San Marcos', 'San Onofre', 'San Pedro', 'Sincé', 'Sincelejo', 'Sucre', 'Tolú', 'Tolú Viejo'] },
        { code: 'TOL', name: 'Tolima', cities: ['Alpujarra', 'Alvarado', 'Ambalema', 'Anzoátegui', 'Armero', 'Ataco', 'Cajamarca', 'Carmen de Apicalá', 'Casabianca', 'Chaparral', 'Coello', 'Coyaima', 'Cunday', 'Dolores', 'El Espinal', 'Falán', 'Flandes', 'Fresno', 'Guamo', 'Herveo', 'Honda', 'Ibagué', 'Icononzo', 'Lérida', 'Líbano', 'Mariquita', 'Melgar', 'Murillo', 'Natagaima', 'Ortega', 'Palocabildo', 'Piedras', 'Planadas', 'Prado', 'Purificación', 'Rioblanco', 'Roncesvalles', 'Rovira', 'Saldaña', 'San Antonio', 'San Luis', 'Santa Isabel', 'Suárez', 'Valle de San Juan', 'Venadillo', 'Villahermosa', 'Villarrica'] },
        { code: 'VAL', name: 'Valle del Cauca', cities: ['Alcalá', 'Andalucía', 'Ansermanuevo', 'Argelia', 'Bolívar', 'Buenaventura', 'Buga', 'Bugalagrande', 'Caicedonia', 'Cali', 'Calima', 'Candelaria', 'Cartago', 'Dagua', 'El Águila', 'El Cairo', 'El Cerrito', 'El Dovio', 'Florida', 'Ginebra', 'Guacarí', 'Jamundí', 'La Cumbre', 'La Unión', 'La Victoria', 'Obando', 'Palmira', 'Pradera', 'Restrepo', 'Riofrío', 'Roldanillo', 'San Pedro', 'Sevilla', 'Toro', 'Trujillo', 'Tuluá', 'Ulloa', 'Versalles', 'Vijes', 'Yotoco', 'Yumbo', 'Zarzal'] },
        { code: 'VAU', name: 'Vaupés', cities: ['Carurú', 'Mitú', 'Taraira', 'Pacoa', 'Papunaua', 'Yavaraté'] },
        { code: 'VID', name: 'Vichada', cities: ['Cumaribo', 'La Primavera', 'Puerto Carreño', 'Santa Rosalía'] },
      ],
      companySettings: {
        company_name: '',
        nit: '',
        phone: '',
        address: '',
        email: '',
        logo_path: '',
        logo_path_dark: '',
      },
    };
  },
  computed: {
    isAdmin() {
      try {
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        return user?.role === 1 || user?.role === '1';
      } catch {
        return false;
      }
    },
    cartSubtotal() {
      // En garantía no hay cobro; en cambio solo suman las prendas nuevas
      if (this.saleMode === 'G') return 0;
      return this.sellableItems.reduce((s, i) => s + i.unit_price * i.quantity, 0);
    },
    sellableItems() {
      if (this.saleMode === 'V') return this.cart;
      return this.cart.filter((i) => i.flow_role === 'new');
    },
    roleOptions() {
      if (this.saleMode === 'C') {
        return [
          { value: 'new', label: 'Prenda nueva (sale)' },
          { value: 'returned', label: 'Prenda devuelta (reingresa)' },
        ];
      }
      if (this.saleMode === 'G') {
        return [
          { value: 'new', label: 'Prenda nueva a entregar' },
          { value: 'damaged', label: 'Prenda dañada (a Imperfectos)' },
        ];
      }
      return [{ value: 'new', label: 'Prenda nueva' }];
    },
    discountAmount() {
      if (this.discountMode === 'percent') {
        return this.cartSubtotal * (Math.min(100, Math.max(0, this.discountValue)) / 100);
      }
      return Math.max(0, this.discountValue);
    },
    invoiceValue() {
      // Valor factura calculado a partir del subtotal y el descuento (no se define manualmente)
      return Math.max(0, this.cartSubtotal - this.discountAmount);
    },
    cartTotal() {
      // Misma fórmula del grand_total en el backend: subtotal - descuento + flete + obsequio
      if (this.saleMode === 'G') return 0;
      return Math.max(0, this.cartSubtotal - this.discountAmount + (this.flete || 0) + (this.giftValue || 0));
    },
    isMultiplePayments() {
      return this.paymentMethod === 'PAGOS MÚLTIPLES';
    },
    multiplePaymentsTotal() {
      return this.multiplePayments.reduce((s, p) => s + (parseFloat(p.amount) || 0), 0);
    },
    multiplePending() {
      if (!this.isMultiplePayments) return 0;
      return Math.max(0, this.cartTotal - this.multiplePaymentsTotal);
    },
    totalPaid() {
      if (this.isMultiplePayments) return this.multiplePaymentsTotal;
      const paid = parseFloat(this.amountPaid);
      return isNaN(paid) ? 0 : paid;
    },
    change() {
      return Math.max(0, this.totalPaid - this.cartTotal);
    },
    availableCities() {
      if (!this.newClient.province) return [];
      const dept = this.departments.find(d => d.name === this.newClient.province);
      return dept ? dept.cities : [];
    },
    cashExpected() {
      const opening = parseFloat(this.cashSession?.opening_amount) || 0;
      const cash = parseFloat(this.cashSummary?.cash_sales) || 0;
      return opening + cash;
    },
  },
  watch: {
    cartTotal(val) {
      if (!this.isProductionOrder && !this.isMultiplePayments) {
        this.amountPaid = Math.max(0, val);
      }
    },
    isProductionOrder(val) {
      this.amountPaid = val ? 0 : Math.max(0, this.cartTotal);
    },
    paymentMethod(val) {
      if (val === 'PAGOS MÚLTIPLES') {
        if (this.multiplePayments.length === 0) {
          this.multiplePayments = [{ payment_method: 'EFECTIVO', amount: Math.max(0, this.cartTotal) }];
        }
      } else {
        this.multiplePayments = [];
      }
    },
  },
  async mounted() {
    this.loadCashSession();
    await this.loadBodegas();
    await this.loadProducts();
    await this.loadClients();
    await this.loadCompanySettings();
  },
  beforeUnmount() {
    this.destroyClientSelect2();
  },
  methods: {
    formatCurrency(val) {
      const n = parseFloat(val) || 0;
      return '$ ' + n.toLocaleString('es-CO', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    stockLabel(product) {
      return Math.floor(product.quantity || 0);
    },
    productPrice(product) {
      return this.priceMode === 'mayor' ? product.sale_price : product.retail_price;
    },
    variantPrice(variant) {
      return this.priceMode === 'mayor' ? variant.wholesale_price : variant.price;
    },
    setSaleMode(mode) {
      this.saleMode = mode;
      // Al cambiar de modo, todas las líneas vuelven a ser prendas nuevas
      this.cart.forEach((i) => { i.flow_role = 'new'; });
    },
    // En garantía la prenda nueva se entrega con valor cero; las devueltas
    // o dañadas no se cobran
    displayUnitPrice(item) {
      if (this.saleMode === 'G') return 0;
      if (this.saleMode === 'C' && item.flow_role !== 'new') return 0;
      return item.unit_price;
    },
    lineTotal(item) {
      return this.displayUnitPrice(item) * item.quantity;
    },
    onSearch() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => this.loadProducts(1), 350);
    },
    onSourceChange() {
      this.loadProducts(1);
    },
    bodegaName(id) {
      const b = this.bodegas.find((x) => x.id == id);
      return b ? b.name : '';
    },
    async loadBodegas() {
      try {
        const data = await this.$store.dispatch('bodegas/getAll');
        this.bodegas = data || [];
      } catch (e) {
        console.error('Error loading bodegas:', e);
      }
    },
    async onBarcodeSearch() {
      // Los lectores de código de barras emulan teclado: con distribución
      // español-latina el guion "-" llega como apóstrofe "'"
      const term = this.search.trim().replace(/'/g, '-');
      if (!term) return;
      const termLower = term.toLowerCase();
      // Versión normalizada sin separadores para comparar barcodes
      const normalize = (val) => String(val).toLowerCase().replace(/[^a-z0-9]/g, '');
      const termNormalized = normalize(term);

      try {
        // Search products by reference, name or barcode
        const params = { search: term, per_page: 100 };
        const res = await posService.getProducts(params);
        const allProducts = res.data?.data || [];

        const matchesTerm = (val) => val != null && String(val).toLowerCase() === termLower;
        const matchesBarcode = (val) => val != null && normalize(val) === termNormalized;

        // 1) Barcode match: always add to cart immediately
        const barcodeProduct = allProducts.find(product => {
          if (matchesBarcode(product.barcode)) return true;
          if (product.variants && product.variants.length > 0) {
            return product.variants.some(v => matchesBarcode(v.barcode));
          }
          return false;
        });

        if (barcodeProduct) {
          let variant = null;
          if (barcodeProduct.variants && barcodeProduct.variants.length > 0) {
            variant = barcodeProduct.variants.find(v => matchesBarcode(v.barcode)) || null;
          }
          // Si el producto tiene variantes pero no se encontro una variante especifica
          // por barcode, abrir el modal de seleccion en vez de agregar el producto padre
          if (!variant && barcodeProduct.has_variants && barcodeProduct.variants && barcodeProduct.variants.length > 0) {
            this.pendingProduct = barcodeProduct;
            this.selectedVariantId = null;
            this.showVariantModal = true;
            this.search = '';
            return;
          }
          this.pushToCart(barcodeProduct, variant);
          this.search = '';
          this.loadProducts(1);
          return;
        }

        // 2) Exact reference/name match: add directly if only one result
        const matchingProducts = allProducts.filter(product => {
          return matchesTerm(product.ref) || matchesTerm(product.name);
        });

        if (matchingProducts.length === 1) {
          const prod = matchingProducts[0];
          // Si el producto tiene variantes, abrir el modal de seleccion
          // en vez de agregar el producto padre sin variante
          if (prod.has_variants && prod.variants && prod.variants.length > 0) {
            this.pendingProduct = prod;
            this.selectedVariantId = null;
            this.showVariantModal = true;
            this.search = '';
            return;
          }
          this.pushToCart(prod, null);
          this.search = '';
          this.loadProducts(1);
        } else if (matchingProducts.length > 1) {
          // Multiple matches: show filtered list for user to click
          this.products = matchingProducts.map((p) => ({ ...p, imgError: false }));
          this.pagination = {
            currentPage: 1,
            lastPage: 1,
            total: matchingProducts.length,
            from: 1,
            to: matchingProducts.length,
          };
        } else {
          Swal.fire({
            icon: 'warning',
            title: 'Producto no encontrado',
            text: `No se encontró ningún producto con: ${term}`,
          });
        }
      } catch (e) {
        console.error('Error searching product:', e);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Error al buscar el producto',
        });
      }
    },
    async loadProducts(page = 1) {
      this.loadingProducts = true;
      try {
        const params = { page, per_page: 12 };
        if (this.search) params.search = this.search.replace(/'/g, '-');
        if (this.sourceFilter) params.source = this.sourceFilter;
        if (this.bodegaFilter) params.bodega_id = this.bodegaFilter;
        const res = await posService.getProducts(params);
        const d = res.data;
        this.products = (d.data || []).map((p) => ({ ...p, imgError: false }));
        this.pagination = {
          currentPage: d.current_page || 1,
          lastPage: d.last_page || 1,
          total: d.total || 0,
          from: d.from || 0,
          to: d.to || 0,
        };
      } catch (e) {
        console.error(e);
      } finally {
        this.loadingProducts = false;
      }
    },
    changePage(page) {
      if (page < 1 || page > this.pagination.lastPage) return;
      this.loadProducts(page);
    },
    async loadClients(selectId = null) {
      try {
        const res = await posService.getClients();
        this.clients = res.data || [];
        if (!this.selectedClientId && this.clients.length) {
          this.selectedClientId = this.clients[0].id;
        }
        await this.$nextTick();
        this.initClientSelect2(selectId);
      } catch (e) {
        console.error(e);
      }
    },
    destroyClientSelect2() {
      const sel = this.$refs.clientSelect;
      if (!sel) return;
      const $s = $(sel);
      if ($s.hasClass('select2-hidden-accessible')) {
        $s.off('change');
        $s.select2('destroy');
      }
    },
    initClientSelect2(selectId = null) {
      const sel = this.$refs.clientSelect;
      if (!sel) return;
      const $s = $(sel);
      this.destroyClientSelect2();
      $s.empty();
      $s.append('<option value="">Seleccionar cliente</option>');
      this.clients.forEach((c) => {
        const label = (c.document ? c.document + ' - ' : '') + c.name;
        $s.append(new Option(label, c.id, false, false));
      });
      $s.select2({
        placeholder: 'Seleccione un cliente',
        allowClear: false,
        width: '100%',
        dropdownParent: $(this.$el).find('.pos-client-select-wrap'),
      });
      $s.on('change', () => {
        const id = $s.val();
        this.selectedClientId = id ? Number(id) : null;
      });
      const idToSet = selectId ?? this.selectedClientId;
      if (idToSet) $s.val(String(idToSet)).trigger('change');
    },
    openClientModal() {
      this.showClientModal = true;
    },
    closeClientModal() {
      this.showClientModal = false;
      this.newClient = {
        name: '',
        document_type: 'CC',
        document: '',
        address: '',
        province: '',
        city: '',
        zip: '',
        phone: '',
        email: '',
        role: '4',
        status: '1',
        payment: '0',
      };
    },
    onProvinceChange() {
      this.newClient.city = '';
    },
    async saveClient() {
      // Solo nombre, documento (cédula) y teléfono son obligatorios;
      // dirección, ciudad, etc. quedan opcionales.
      const missing = [];
      if (!String(this.newClient.name || '').trim()) missing.push('nombre');
      if (!String(this.newClient.document || '').trim()) missing.push('documento (cédula)');
      if (!String(this.newClient.phone || '').trim()) missing.push('teléfono');
      if (missing.length) {
        Swal.fire({
          icon: 'warning',
          title: 'Campos obligatorios',
          text: `Faltan: ${missing.join(', ')}`
        });
        return;
      }
      this.savingClient = true;
      try {
        const res = await posService.createClient(this.newClient);
        const newClient = res.data;
        this.clients.push(newClient);
        this.selectedClientId = newClient.id;
        this.closeClientModal();
        await this.loadClients(newClient.id);
        Swal.fire({
          icon: 'success',
          title: 'Éxito',
          text: 'Cliente creado correctamente'
        });
      } catch (e) {
        console.error(e);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: e.response?.data?.message || 'Error al crear cliente'
        });
      } finally {
        this.savingClient = false;
      }
    },
    addToCart(product) {
      if (product.has_variants && product.variants && product.variants.length > 0) {
        this.pendingProduct = product;
        this.selectedVariantId = null;
        this.showVariantModal = true;
        return;
      }
      this.pushToCart(product, null);
    },
    cancelVariantModal() {
      this.showVariantModal = false;
      this.pendingProduct = null;
      this.selectedVariantId = null;
    },
    confirmVariant() {
      if (!this.selectedVariantId || !this.pendingProduct) return;
      const variant = this.pendingProduct.variants.find((v) => v.id === this.selectedVariantId);
      if (!variant) return;
      this.pushToCart(this.pendingProduct, variant);
      this.cancelVariantModal();
    },
    pushToCart(product, variant) {
      const productSource = product.source || 'local';
      if (this.cart.length > 0) {
        const cartSource = this.cart[0].source || 'local';
        if (cartSource !== productSource) {
          Swal.fire(
            'Mezcla no permitida',
            `El carrito tiene productos ${cartSource.toUpperCase()}. No puede agregar productos ${productSource.toUpperCase()}.`,
            'warning'
          );
          return;
        }
      }

      const cartKey  = variant ? `${product.id}_${variant.id}` : String(product.id);
      const label    = variant ? `${product.name} – ${variant.title}` : product.name;
      const price    = this.priceMode === 'mayor'
        ? parseFloat((variant ? (variant.wholesale_price || product.sale_price) : product.sale_price) || 0)
        : parseFloat((variant ? (variant.price || product.retail_price) : product.retail_price) || 0);
      const maxStock = variant ? variant.inventory_quantity : product.quantity;
      const existing = this.cart.find((i) => i.cart_key === cartKey);
      if (existing) {
        existing.quantity += 1;
        return;
      }
      this.cart.push({
        cart_key:       cartKey,
        inventory_id:   product.id,
        variant_id:     variant ? variant.id : null,
        bodega_id:      this.bodegaFilter || null,
        name:           label,
        ref:            product.ref,
        color:          variant ? (variant.option2 || '') : '',
        talla:          variant ? (variant.option1 || '') : '',
        requires_variant: !!(product.has_variants && product.variants && product.variants.length > 0),
        source:         product.source || 'local',
        unit_price:     price,
        quantity:       1,
        manage_stock:   product.manage_stock,
        max_stock:      maxStock,
        flow_role:      'new',
      });
    },
    removeFromCart(index) {
      this.cart.splice(index, 1);
    },
    changeQty(index, delta) {
      const item = this.cart[index];
      const newQty = item.quantity + delta;
      if (newQty < 1) { this.removeFromCart(index); return; }
      item.quantity = newQty;
    },
    onEvidenceChange(event) {
      const file = event.target.files[0];
      if (file) {
        this.evidenceFile = file;
        this.evidencePreview = URL.createObjectURL(file);
      } else {
        this.evidenceFile = null;
        this.evidencePreview = null;
      }
    },
    addMultiplePayment() {
      this.multiplePayments.push({ payment_method: 'EFECTIVO', amount: 0 });
    },
    removeMultiplePayment(idx) {
      this.multiplePayments.splice(idx, 1);
    },
    buildPaymentsPayload() {
      if (!this.isMultiplePayments) return null;
      if (this.multiplePayments.length === 0) {
        Swal.fire('Pagos incompletos', 'Agregue al menos un pago.', 'warning');
        return null;
      }
      const invalid = this.multiplePayments.find(
        (p) => !p.payment_method || !(parseFloat(p.amount) > 0)
      );
      if (invalid) {
        Swal.fire('Pagos incompletos', 'Cada pago debe tener método de pago y valor mayor a cero.', 'warning');
        return null;
      }
      return this.multiplePayments.map((p) => ({
        payment_method: p.payment_method,
        amount: Math.round((parseFloat(p.amount) || 0) * 100) / 100,
      }));
    },
    toMovementPayload(lines) {
      return lines.map((i) => ({
        inventory_id: i.inventory_id,
        variant_id:   i.variant_id || null,
        bodega_id:    i.bodega_id || null,
        quantity:     i.quantity,
      }));
    },
    async saveSale() {
      if (this.cart.length === 0) return;
      if (!this.selectedClientId) {
        Swal.fire('Cliente requerido', 'Seleccione un cliente.', 'warning');
        return;
      }
      const mode = this.saleMode;
      const newItems = this.sellableItems;
      const returnedItems = mode === 'C'
        ? this.cart.filter((i) => i.flow_role === 'returned')
        : [];
      const damagedItems = mode === 'G'
        ? this.cart.filter((i) => i.flow_role === 'damaged')
        : [];

      // Cambio/garantía siempre entregan al menos una prenda nueva
      if (mode !== 'V' && newItems.length === 0) {
        Swal.fire(
          'Falta la prenda a entregar',
          mode === 'G'
            ? 'Agrega la prenda nueva que se entregará en garantía.'
            : 'Agrega la prenda nueva que se entregará en el cambio.',
          'warning'
        );
        return;
      }
      // Validar que los items de productos CON variantes tengan variante
      // seleccionada. Los productos sin variantes se venden sin variante.
      const missingVariant = this.cart.find(
        (i) => i.requires_variant && !i.variant_id
      );
      if (missingVariant) {
        Swal.fire(
          'Variante requerida',
          `El producto "${missingVariant.name}" no tiene una variante seleccionada (talla/color). Remuévalo del carrito y agréguelo nuevamente seleccionando la variante.`,
          'warning'
        );
        return;
      }
      const paymentsPayload = mode === 'G' ? null : this.buildPaymentsPayload();
      if (paymentsPayload === null && this.isMultiplePayments) return;
      this.saving = true;
      try {
        const client = this.clients.find((c) => c.id == this.selectedClientId);
        const totalPaidMultiple = paymentsPayload
          ? paymentsPayload.reduce((s, p) => s + p.amount, 0)
          : 0;
        const res = await posService.storeSale({
          client_id:       this.selectedClientId,
          payment_method:  mode === 'G' ? 'GARANTIA' : (paymentsPayload ? 'MULTIPLE' : this.paymentMethod),
          discount_amount: mode === 'G' ? 0 : this.discountAmount,
          gift_value:      mode === 'G' ? 0 : (this.giftValue || 0),
          amount_paid:     mode === 'G'
            ? 0
            : (paymentsPayload
              ? totalPaidMultiple
              : ((this.amountPaid !== null && this.amountPaid !== '' && !isNaN(this.amountPaid)) ? Number(this.amountPaid) : this.cartTotal)),
          payments:        paymentsPayload || undefined,
          notes:           this.observation || '',
          is_production_order: this.isProductionOrder ? 1 : 0,
          pos_sale_type:   mode,
          allow_negative_stock: this.allowNegativeStock,
          flete:           mode === 'G' ? 0 : (parseFloat(this.flete) || 0),
          items: newItems.map((i) => ({
            inventory_id: i.inventory_id,
            variant_id:   i.variant_id || null,
            bodega_id:    i.bodega_id || null,
            quantity:     i.quantity,
            // En garantía la prenda nueva sale con valor cero
            unit_price:   mode === 'G' ? 0 : i.unit_price,
          })),
          returned_items: returnedItems.length ? this.toMovementPayload(returnedItems) : undefined,
          damaged_items:  damagedItems.length ? this.toMovementPayload(damagedItems) : undefined,
        });
        const remissionId = res?.data?.id;
        if (remissionId) {
          const ticketPayload = buildTicketPayload({
            remissionId,
            client,
            cart: newItems.map((i) => ({
              ...i,
              unit_price: mode === 'G' ? 0 : i.unit_price,
            })),
            totals: {
              subtotal: this.cartSubtotal,
              discountAmount: mode === 'G' ? 0 : this.discountAmount,
              flete: mode === 'G' ? 0 : (parseFloat(this.flete) || 0),
              giftValue: mode === 'G' ? 0 : this.giftValue,
              total: this.cartTotal,
            },
            paymentMethod: mode === 'G' ? 'GARANTIA' : this.paymentMethod,
            amountPaid: mode === 'G' ? 0 : (paymentsPayload ? totalPaidMultiple : (this.amountPaid || this.cartTotal)),
            payments: paymentsPayload || [],
            sellerName: this.getUserName(),
            company: this.companySettings,
          });
          savePosTicketData(ticketPayload);

          // Subir evidencia de pago via media service (igual que remissions/create)
          if (this.evidenceFile) {
            try {
              const formData = new FormData();
              formData.append('mediaable_id', remissionId);
              formData.append('mediaable_type', 'remission');
              formData.append('file', this.evidenceFile);
              await mediaService.createData(formData);
            } catch (evidenceErr) {
              console.error('Error al subir evidencia:', evidenceErr);
            }
          }
        }
        const successTitle = mode === 'G' ? 'Garantía registrada'
          : (mode === 'C' ? 'Cambio registrado' : 'Venta guardada');
        const successText = mode === 'G'
          ? 'La garantía fue registrada: salida con valor $0 y prenda dañada enviada a Imperfectos.'
          : (mode === 'C'
            ? 'El cambio fue registrado: devolución reintegrada al inventario.'
            : 'La venta fue registrada correctamente.');
        await Swal.fire(successTitle, successText, 'success');
        this.cart = [];
        this.discountValue = 0;
        this.flete = 0;
        this.giftValue = 0;
        this.observation = '';
        this.isProductionOrder = false;
        this.saleType = null;
        this.amountPaid = mode === 'G' ? 0 : this.amountPaid;
        if (this.paymentMethod === 'PAGOS MÚLTIPLES') {
          this.multiplePayments = [{ payment_method: 'EFECTIVO', amount: 0 }];
        }
        this.evidenceFile = null;
        this.evidencePreview = null;
        this.loadProducts(1);
        if (remissionId) {
          openPosTicketWindow(this.$router, remissionId);
        }
      } catch (e) {
        const msg = e?.response?.data?.message || 'No se pudo guardar la venta.';
        Swal.fire('Error', msg, 'error');
      } finally {
        this.saving = false;
      }
    },
    // ===== Caja (apertura / cierre) =====
    formatDateTime(val) {
      if (!val) return '—';
      return new Date(val).toLocaleString('es-CO', { dateStyle: 'short', timeStyle: 'short' });
    },
    async loadCashSession() {
      try {
        const res = await posService.getCurrentCashSession();
        this.cashSession = res.data?.data || null;
        this.cashSummary = res.data?.summary || null;
      } catch (e) {
        console.error('Error cargando sesión de caja:', e);
        this.cashSession = null;
        this.cashSummary = null;
      } finally {
        this.cashLoaded = true;
      }
    },
    async openCash() {
      this.openingCash = true;
      try {
        const res = await posService.openCashSession({
          opening_amount: parseFloat(this.openForm.opening_amount) || 0,
        });
        this.cashSession = res.data?.data || null;
        this.cashSummary = { total_sales: 0, cash_sales: 0, sales_count: 0 };
        this.openForm = { opening_amount: 0 };
        Swal.fire({
          icon: 'success',
          title: 'Caja abierta',
          text: `Puedes comenzar a vender. Monto inicial: ${this.formatCurrency(this.cashSession.opening_amount)}`,
          timer: 1800,
          showConfirmButton: false,
        });
      } catch (e) {
        Swal.fire('Error', e?.response?.data?.message || 'No se pudo abrir la caja.', 'error');
      } finally {
        this.openingCash = false;
      }
    },
    async openCloseCash() {
      this.closeForm = { closing_amount: '', notes: '' };
      // Refrescar la sesión para mostrar las ventas actualizadas al momento de cerrar
      await this.loadCashSession();
      this.showCloseCash = true;
    },
    async confirmCloseCash() {
      if (this.closeForm.closing_amount === '' || isNaN(parseFloat(this.closeForm.closing_amount))) {
        Swal.fire('Atención', 'Ingresa el efectivo contado en caja.', 'warning');
        return;
      }
      this.closingCash = true;
      try {
        const res = await posService.closeCashSession(this.cashSession.id, {
          closing_amount: parseFloat(this.closeForm.closing_amount),
          notes: this.closeForm.notes || '',
        });
        const d = res.data?.data || {};
        const diff = parseFloat(d.difference) || 0;
        this.cashSession = null;
        this.cashSummary = null;
        this.showCloseCash = false;
        const diffHtml = diff === 0
          ? '<span class="text-success">Caja cuadrada</span>'
          : (diff > 0
            ? `<span class="text-success">Sobrante: ${this.formatCurrency(diff)}</span>`
            : `<span class="text-danger">Faltante: ${this.formatCurrency(Math.abs(diff))}</span>`);
        Swal.fire({
          icon: diff === 0 ? 'success' : 'warning',
          title: 'Caja cerrada',
          html: `
            <div class="text-start text-sm">
              <div>Ventas totales: <b>${this.formatCurrency(d.total_sales)}</b></div>
              <div>Ventas en efectivo: <b>${this.formatCurrency(d.cash_sales)}</b></div>
              <div>Efectivo esperado: <b>${this.formatCurrency(d.expected_cash)}</b></div>
              <div>Efectivo contado: <b>${this.formatCurrency(d.closing_amount)}</b></div>
              <hr class="my-2">
              <div>${diffHtml}</div>
            </div>`,
          confirmButtonText: 'Aceptar',
        });
      } catch (e) {
        Swal.fire('Error', e?.response?.data?.message || 'No se pudo cerrar la caja.', 'error');
      } finally {
        this.closingCash = false;
      }
    },
    getUserName() {
      try {
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        return user?.name || null;
      } catch {
        return null;
      }
    },
    async loadCompanySettings() {
      try {
        const res = await settingsService.get();
        const data = res?.data || {};
        this.companySettings = {
          company_name: data.company_name || '',
          nit: data.nit || '',
          phone: data.phone || '',
          address: data.address || '',
          email: data.email || '',
          logo_path: data.logo_path || '',
          logo_path_dark: data.logo_path_dark || '',
        };
      } catch (e) {
        console.error('No se pudo cargar configuración de empresa:', e);
      }
    },
  },
};
</script>

<style scoped>
.pos-page {
  min-height: calc(100vh - 80px);
  position: relative;
}

/* ===== Panel caja cerrada ===== */
/* Ocupa el mismo cuadro del cuerpo donde van productos y carrito;
   nunca tapa el menú lateral ni el navbar. */
.cash-closed-frame {
  width: 100%;
  min-height: calc(100vh - 220px);
  padding: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
}
.cash-block-card {
  width: 100%;
  max-width: 360px;
  text-align: center;
}
.cash-block-icon {
  font-size: 42px;
  color: #11cdef;
}

.cash-summary {
  border: 1px dashed #d2d2d2;
  border-radius: 8px;
  padding: .6rem .75rem;
  background: #fafafa;
}

.pos-layout {
  display: grid;
  grid-template-columns: 1fr 340px;
  gap: 1rem;
  align-items: start;
}

@media (max-width: 900px) {
  .pos-layout { grid-template-columns: 1fr; }
  .pos-cart { order: -1; }
}

/* ---- Products grid ---- */
.pos-products-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: .75rem;
  flex-wrap: wrap;
}
.pos-products-toolbar {
  display: flex;
  gap: .5rem;
  flex-wrap: wrap;
}
.pos-search { width: 200px; }
.pos-bodega-select { width: 160px; }
.pos-source-select { width: 130px; }

.pos-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: .75rem;
}
@media (max-width: 1400px) { .pos-grid { grid-template-columns: repeat(3, 1fr); } }
@media (max-width: 768px) { .pos-grid { grid-template-columns: repeat(2, 1fr); } }

.pos-product-card {
  position: relative;
  display: block;
  aspect-ratio: 4 / 5;
  padding: 0;
  border: none;
  border-radius: 12px;
  overflow: hidden;
  background: #f1f2f4;
  cursor: pointer;
  transition: transform .15s, box-shadow .15s;
  box-shadow: 0 1px 3px rgba(0,0,0,.08);
}
.pos-product-card:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(0,0,0,.18);
}
.pos-product-card:disabled { opacity: .5; cursor: not-allowed; }

.pos-product-img {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.pos-product-placeholder-icon {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 34px;
  color: #b5b9c0;
}

.pos-source-badge {
  position: absolute;
  top: 8px;
  left: 8px;
  font-size: 9px;
  font-weight: 700;
  padding: 2px 7px;
  border-radius: 20px;
  color: #fff;
  letter-spacing: .4px;
}
.pos-source-badge--local   { background: rgba(131,146,171,.9); }
.pos-source-badge--shopify { background: rgba(149,191,72,.95); }

.pos-stock-badge {
  position: absolute;
  top: 8px;
  right: 8px;
  background: rgba(0,0,0,.55);
  backdrop-filter: blur(3px);
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  min-width: 22px;
  height: 20px;
  padding: 0 7px;
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.pos-product-card--low .pos-stock-badge { background: #f5365c; }

.pos-product-info {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  padding: 30px 10px 10px;
  background: linear-gradient(to bottom, rgba(0,0,0,0), rgba(0,0,0,.78));
  text-align: left;
}
.pos-product-card--noimg .pos-product-info {
  padding: 10px;
  background: rgba(255,255,255,.88);
  backdrop-filter: blur(4px);
}
.pos-product-name {
  font-size: 12px;
  font-weight: 600;
  color: #fff;
  margin: 0 0 2px;
  line-height: 1.25;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.pos-product-card--noimg .pos-product-name { color: #1a1a1a; }
.pos-product-price {
  font-size: 13px;
  font-weight: 700;
  color: #fff;
  opacity: .92;
}
.pos-product-card--noimg .pos-product-price { color: #1a1a1a; opacity: 1; }

/* ---- Cart ---- */
.pos-cart { position: sticky; top: 80px; max-height: calc(100vh - 90px); overflow-y: auto; }

.pos-cart-items { overflow-y: auto; max-height: 260px; }

.pos-cart-card {
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 10px;
  padding: .65rem .75rem;
  margin-bottom: .6rem;
  box-shadow: 0 1px 4px rgba(0,0,0,.06);
}
.pos-cart-card-header {
  display: flex;
  align-items: center;
  gap: .4rem;
}
.pos-cart-card-bodega {
  font-size: 11px;
  color: #6c757d;
  margin-top: 2px;
}
.pos-cart-item-badge {
  font-size: 9px;
  font-weight: 700;
  padding: 2px 6px;
  border-radius: 4px;
  color: #fff;
  flex-shrink: 0;
  letter-spacing: .4px;
}
.pos-cart-item-badge--local   { background: #8392ab; }
.pos-cart-item-badge--shopify { background: #96bf48; }
.pos-cart-card-name { font-size: 12px; font-weight: 600; flex: 1; line-height: 1.3; }
.pos-cart-card-remove {
  background: none;
  border: none;
  color: #f5365c;
  cursor: pointer;
  padding: 0;
  font-size: 13px;
  line-height: 1;
  flex-shrink: 0;
}
.pos-cart-card-qty-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.pos-cart-card-qty { display: flex; align-items: center; gap: .3rem; }
.pos-qty-btn {
  width: 28px;
  height: 28px;
  border-radius: 6px;
  border: 1px solid #dee2e6;
  background: #fff;
  font-size: 15px;
  line-height: 1;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
}
.pos-qty-btn:hover { background: #f0f0f0; }
.pos-qty-value { font-size: 14px; font-weight: 700; min-width: 26px; text-align: center; }
.pos-cart-card-prices {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: .5rem;
}
.pos-cart-price-field { flex: 1; }
.pos-cart-price-label { display: block; font-size: 10px; color: #8392ab; margin-bottom: 2px; }
.pos-cart-unit-price-value { font-size: 13px; font-weight: 600; color: #1A1A1A; }
.pos-cart-price-total { font-size: 14px; font-weight: 700; color: #82d616; }

.pos-total-amount { font-size: 1.2rem; font-weight: 800; color: #82d616; }

/* ---- Pagos múltiples ---- */
.pos-sale-mode .btn { margin-bottom: 0 !important; }
.pos-role-select { font-size: 11px; padding: 4px 8px; }
.pos-neg-stock-switch .form-check-input { margin-top: 0; cursor: pointer; }
.pos-neg-stock-switch .form-check-label { cursor: pointer; white-space: nowrap; }
.pos-multiple-payments {
  border: 1px dashed #d2d2d2;
  border-radius: 8px;
  padding: .6rem;
  background: #fafafa;
}
.pos-payment-method-select { flex: 1.1; min-width: 0; }
.pos-payment-amount-input  { flex: 1; min-width: 0; }
.pos-payment-remove {
  background: none;
  border: none;
  color: #f5365c;
  cursor: pointer;
  padding: 6px 2px;
  font-size: 13px;
  line-height: 1;
  flex-shrink: 0;
}
.pos-payment-remove:hover { color: #d02c4e; }

.pos-pagination { display: flex; align-items: center; gap: .25rem; }

/* ---- Modal overlay ---- */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,.45);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
}
.modal-box { width: 100%; max-width: 420px; border-radius: 12px; }

/* ---- Variant modal ---- */
.pos-variant-list {
  display: flex;
  flex-direction: column;
  gap: .4rem;
  max-height: 340px;
  overflow-y: auto;
}
.pos-variant-btn {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: .5rem;
  padding: .55rem .75rem;
  border: 1.5px solid #dee2e6;
  border-radius: 8px;
  background: #fff;
  cursor: pointer;
  text-align: left;
  transition: border-color .15s, background .15s;
}
.pos-variant-btn:hover:not(:disabled) { border-color: #82d616; background: #f6fff0; }
.pos-variant-btn--selected { border-color: #82d616 !important; background: #f0ffe0 !important; }
.pos-variant-btn--out { opacity: .45; cursor: not-allowed; }
.pos-variant-title { font-size: 13px; font-weight: 600; flex: 1; }
.pos-variant-meta  { font-size: 12px; white-space: nowrap; }
</style>

<style>
/* Los SweetAlert se muestran por encima de los modales del POS (z-index 9999) */
.swal2-container { z-index: 10000 !important; }
</style>
